import type { AgentMessage, Message, UserMessage } from "@/hooks/use-stream-conversation";

// Message type constants
export const MESSAGE_TYPES = {
    AGENT: "agent",
    USER: "user",
} as const;

export type MessageType = "agent" | "user";

/**
 * Filters messages by their type
 * @param messages - Array of messages to filter
 * @param type - The message type to filter by
 * @returns Array of messages of the specified type with proper typing
 */
export const filterMessagesByType = <T extends MessageType>(
    messages: Message[],
    type: T,
): T extends typeof MESSAGE_TYPES.AGENT
    ? AgentMessage[]
    : T extends typeof MESSAGE_TYPES.USER
      ? UserMessage[]
      : Message[] => {
    return messages.filter((message) => message.type === type) as ReturnType<typeof filterMessagesByType<T>>;
};

/**
 * Determines if a user message fragment is still pending by checking if the agent
 * has acknowledged seeing the user input block that contains this fragment.
 *
 * A fragment is considered pending when the agent hasn't sent a "userInputSeen"
 * message for the corresponding user input block, indicating the agent hasn't
 * processed that user input yet.
 *
 * @param fragmentId - The ID of the fragment to check
 * @param agentMessages - Array of agent messages to search for "userInputSeen" events
 * @returns true if the fragment is pending (not seen by agent), false otherwise
 */
export const isUserInputFragmentPending = (fragmentId: string, agentMessages?: AgentMessage[]): boolean => {
    // Extract all seen user input block IDs from agent messages
    const seenUserInputBlockIds = new Set<string>();
    if (agentMessages) {
        for (const message of agentMessages) {
            if (message.type === "agent") {
                for (const fragment of message.fragments) {
                    if (fragment.type === "userInputSeen") {
                        seenUserInputBlockIds.add(`input-${fragment.value.id}`);
                    }
                }
            }
        }
    }

    // Check if this fragment hasn't been seen by the agent
    // For the new inputs array format, fragment IDs are like "input-xyz-text-0" or "input-xyz-image-1"
    // The userInputSeen event references the block ID "input-xyz"
    // So we check both exact match and prefix match (for multi-input blocks)
    if (seenUserInputBlockIds.has(fragmentId)) {
        return false;
    }

    // Check if any seen block ID is a prefix of this fragment ID
    for (const seenId of seenUserInputBlockIds) {
        if (fragmentId.startsWith(seenId + "-")) {
            return false;
        }
    }

    return true;
};
